/* =========================================================================
   Change Order — Create (v2)
   Ported from docs/featureBriefs/design_handoff_work_orders_change_orders/prototype/app/work-orders.js
   (PAGES["co-create"]).
   ========================================================================= */
import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, ClipboardList, FileText, Layers } from "lucide-react";
import "./WorkOrders.css";
import { useSiteContext } from "../../../SiteContext";
import { Page } from "../../../interfaces/PagesInterface";
import ChangeOrdersManager from "../../../managers/ChangeOrdersManager";
import { getNextCORNumber } from "../../../api/changeOrders";
import { WorkOrderListItem } from "../../../interfaces/WorkOrderInterface";
import {
    BackBar,
    Banner,
    Ck,
    DisabledNote,
    EmptyState,
    PageHead,
    SectionLabel,
    WO_STATUS_META,
    money,
    useToast,
    woNumText,
} from "./woShared";

const ChangeOrderCreatePage: React.FC = () => {
    const { setPage } = useSiteContext();
    const { toastNode, showToast } = useToast();

    const [job, setJob] = useState(ChangeOrdersManager.selectedJobNumber || "");
    const [eligible, setEligible] = useState<WorkOrderListItem[]>([]);
    const [eligibleLoading, setEligibleLoading] = useState(false);
    const [eligibleError, setEligibleError] = useState<string | null>(null);
    const [picked, setPicked] = useState<Set<number>>(new Set());
    const [notes, setNotes] = useState("");
    // TAS-304 (D8): COR# pre-fills with the next in sequence for the job and can be
    // overridden before submit (to match Dwayne's Excel log). corTouched tracks
    // whether the user edited it — untouched means let the server auto-assign, so
    // a stale suggestion can never collide.
    const [corNumber, setCorNumber] = useState("");
    const [corSuggested, setCorSuggested] = useState<number | null>(null);
    const [corTouched, setCorTouched] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const trimmedJob = job.trim();

    const fetchEligible = useCallback(async (jobNumber: string) => {
        setEligibleLoading(true);
        setEligibleError(null);
        try {
            const data = await ChangeOrdersManager.getEligibleWorkOrders(jobNumber);
            setEligible(data || []);
        } catch (err) {
            console.error("Error fetching eligible work orders", err);
            setEligible([]);
            setEligibleError("Failed to load work orders for this job.");
        } finally {
            setEligibleLoading(false);
        }
    }, []);

    // Debounce the eligible-WO fetch while the job number is typed; clear any
    // stale selection whenever the job changes (mirrors the prototype's
    // App.coJobInput, which resets App.WO.coPick on every keystroke).
    useEffect(() => {
        setPicked(new Set());
        if (!trimmedJob) {
            setEligible([]);
            setEligibleError(null);
            setEligibleLoading(false);
            return;
        }
        const t = window.setTimeout(() => {
            fetchEligible(trimmedJob);
            setCorTouched(false);
            getNextCORNumber(trimmedJob)
                .then((r) => { setCorSuggested(r.nextCORNumber); setCorNumber(String(r.nextCORNumber)); })
                .catch(() => { setCorSuggested(null); setCorNumber(""); });
        }, 400);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trimmedJob, fetchEligible]);

    const togglePick = (id: number) => {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const pickedRows = eligible.filter((w) => picked.has(w.workOrderID));
    const pickedTotal = pickedRows.reduce((sum, w) => sum + (w.totalAmount ?? 0), 0);
    const corValue = corNumber.trim() === "" ? null : Number(corNumber);
    const corInvalid = corValue !== null && (!Number.isInteger(corValue) || corValue < 1);
    const canCreate = !!trimmedJob && picked.size > 0 && !corInvalid;

    const handleCreate = async () => {
        if (!canCreate || creating) return;
        setCreating(true);
        setCreateError(null);
        try {
            const result = await ChangeOrdersManager.createChangeOrder({
                jobNumber: trimmedJob,
                workOrderIDs: Array.from(picked),
                notes: notes.trim() || undefined,
                // Only send an override the user actually typed; otherwise the
                // server assigns atomically (immune to a stale suggestion).
                ...(corTouched && corValue !== null ? { corNumber: corValue } : {}),
            });
            showToast("Change order created.");
            if (result?.changeOrderID) {
                ChangeOrdersManager.selectedChangeOrderID = result.changeOrderID;
                setPage(Page.ChangeOrderDetail);
            } else {
                setPage(Page.ChangeOrders);
            }
        } catch (err: any) {
            setCreateError(err?.response?.data?.message || err?.message || "Failed to create change order.");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="wov2">
            <BackBar label="Change Orders" onBack={() => setPage(Page.ChangeOrders)} />
            <PageHead
                Icon={Layers}
                title="Create Change Order"
                sub="Pick the work orders on a job to roll into one change order. The COR number defaults to the next in sequence and can be overridden before submitting."
            />

            <div className="card pad" style={{ marginBottom: 18 }}>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                    <div className="field" style={{ maxWidth: 320, flex: "1 1 220px" }}>
                        <label>Job Number</label>
                        <input
                            className="input"
                            value={job}
                            placeholder="e.g. 8821"
                            onChange={(e) => setJob(e.target.value)}
                        />
                    </div>
                    <div className="field" style={{ maxWidth: 200 }}>
                        <label>COR #</label>
                        <input
                            className="input"
                            value={corNumber}
                            placeholder={trimmedJob ? "…" : "—"}
                            disabled={!trimmedJob}
                            inputMode="numeric"
                            onChange={(e) => {
                                setCorNumber(e.target.value.replace(/[^0-9]/g, ""));
                                setCorTouched(true);
                            }}
                        />
                        <span className="sub" style={{ marginTop: 4 }}>
                            {corInvalid
                                ? "Enter a whole number of 1 or more."
                                : corSuggested !== null && corTouched && corValue !== null && corValue !== corSuggested
                                    ? `Override — next in sequence is ${corSuggested}. Can't be changed after submit.`
                                    : corSuggested !== null
                                        ? "Next in sequence. Override it to match an existing log number."
                                        : "Assigned automatically on create."}
                        </span>
                    </div>
                </div>
            </div>

            <SectionLabel Icon={ClipboardList}>
                Eligible work orders{trimmedJob ? ` · Job #${trimmedJob}` : ""}
            </SectionLabel>
            <div className="card pad" style={{ marginBottom: 18 }}>
                {!trimmedJob ? (
                    <EmptyState Icon={ClipboardList} title="Enter a job number to choose its work orders">
                        Only work orders on that job that aren't voided, no-charge, or already in another change order are eligible.
                    </EmptyState>
                ) : eligibleLoading ? (
                    <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading eligible work orders…</div>
                ) : eligibleError ? (
                    <Banner
                        variant="danger"
                        Icon={AlertTriangle}
                        action={<button className="btn sm" onClick={() => fetchEligible(trimmedJob)} type="button">Retry</button>}
                    >
                        {eligibleError}
                    </Banner>
                ) : eligible.length ? (
                    eligible.map((w) => (
                        <div
                            key={w.workOrderID}
                            className={`pick-row ${picked.has(w.workOrderID) ? "on" : ""}`}
                            onClick={() => togglePick(w.workOrderID)}
                        >
                            <Ck on={picked.has(w.workOrderID)} onClick={(e) => { e.stopPropagation(); togglePick(w.workOrderID); }} />
                            <div className="grow">
                                <div className="pr-t">{woNumText(w)} — {w.firstItemDescription ?? ""}</div>
                                <div className="pr-s">{w.employeeName} · {WO_STATUS_META[w.status]?.short ?? ""} · {w.projectName}</div>
                            </div>
                            <span className="strong mono">{money(w.totalAmount)}</span>
                        </div>
                    ))
                ) : (
                    <EmptyState Icon={AlertTriangle} title={`No eligible work orders for Job #${trimmedJob}`}>
                        Every work order on this job is either voided, no-charge, or already in a change order.
                    </EmptyState>
                )}
            </div>

            <SectionLabel Icon={FileText}>Notes</SectionLabel>
            <div className="card pad" style={{ marginBottom: 18 }}>
                <textarea
                    className="input"
                    placeholder="Optional context for this change order…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </div>

            {createError && <Banner variant="danger" Icon={AlertTriangle}>{createError}</Banner>}

            <div className="actionrow">
                <span className="muted">{picked.size} work order{picked.size === 1 ? "" : "s"} selected · {money(pickedTotal)}</span>
                <span className="grow" />
                <button className="btn ghost" onClick={() => setPage(Page.ChangeOrders)} type="button">Cancel</button>
                <button className="btn primary" disabled={!canCreate || creating} onClick={handleCreate} type="button">
                    <Check size={15} />{creating ? "Creating…" : "Create Change Order"}
                </button>
                {!canCreate && <DisabledNote>Enter a job number and select at least one work order.</DisabledNote>}
            </div>

            {toastNode}
        </div>
    );
};

export default ChangeOrderCreatePage;
